import { useEffect, useState } from "react";
import { RefreshCw, BarChart3, MousePointerClick, Eye, TrendingUp, AlertCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminGsc() {
  const [days, setDays] = useState("28");
  const [data, setData] = useState(null);
  const [queries, setQueries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (refresh = false, d = days) => {
    setLoading(true);
    setError(null);
    try {
      const [pagesRes, queriesRes] = await Promise.all([
        api.get(`/admin/gsc/pages?days=${d}&refresh=${refresh}`),
        api.get(`/admin/gsc/queries?days=${d}&refresh=${refresh}`),
      ]);
      setData(pagesRes.data);
      setQueries(queriesRes.data);
    } catch (e) {
      const status = e.response?.status;
      setError({
        status,
        message: formatApiErrorDetail(e.response?.data?.detail),
      });
      setData(null);
      setQueries(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const rows = (data?.rows || [])
    .slice()
    .sort((a, b) => b.clicks - a.clicks || b.impressions - a.impressions);
  const totals = rows.reduce(
    (acc, r) => ({
      clicks: acc.clicks + r.clicks,
      impressions: acc.impressions + r.impressions,
    }),
    { clicks: 0, impressions: 0 }
  );

  const pageLabel = (url) => {
    try {
      return new URL(url).pathname || "/";
    } catch {
      return url;
    }
  };

  return (
    <div data-testid="admin-gsc">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-semibold text-2xl text-brand-navy">
            Performances Google
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Données Search Console par page : clics, impressions, CTR et position moyenne.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40" data-testid="gsc-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7" data-testid="gsc-period-7">7 jours</SelectItem>
              <SelectItem value="28" data-testid="gsc-period-28">28 jours</SelectItem>
              <SelectItem value="90" data-testid="gsc-period-90">90 jours</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => load(true)}
            disabled={loading}
            data-testid="gsc-refresh-btn"
            aria-label="Rafraîchir depuis Google"
            title="Forcer l'actualisation depuis Google"
          >
            <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
          </Button>
        </div>
      </div>

      {error && error.status === 503 && (
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900"
          data-testid="gsc-not-configured"
        >
          <div className="flex items-center gap-2 font-semibold mb-2">
            <AlertCircle className="w-4 h-4" /> Search Console n'est pas encore connectée
          </div>
          <p className="mb-2">Pour activer le suivi des performances :</p>
          <ol className="list-decimal ml-5 space-y-1 text-amber-800">
            <li>Créez un compte de service dans Google Cloud Console et activez l'API Search Console.</li>
            <li>Ajoutez l'email du compte de service comme utilisateur de la propriété serrurier-lagny-sur-marne.com dans Search Console.</li>
            <li>Fournissez la clé JSON du compte de service — elle sera ajoutée côté serveur, jamais côté navigateur.</li>
          </ol>
        </div>
      )}

      {error && error.status !== 503 && (
        <div
          className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700"
          data-testid="gsc-error"
        >
          {error.message}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Clics", value: totals.clicks.toLocaleString("fr-FR"), icon: MousePointerClick, tid: "gsc-total-clicks" },
              { label: "Impressions", value: totals.impressions.toLocaleString("fr-FR"), icon: Eye, tid: "gsc-total-impressions" },
              {
                label: "CTR moyen",
                value: totals.impressions ? ((totals.clicks / totals.impressions) * 100).toFixed(1) + " %" : "—",
                icon: TrendingUp,
                tid: "gsc-avg-ctr",
              },
              { label: "Pages vues par Google", value: rows.length, icon: BarChart3, tid: "gsc-page-count" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-brand-line bg-white p-5 shadow-sm"
                data-testid={s.tid}
              >
                <s.icon className="w-4 h-4 text-brand-golddark mb-2" />
                <div className="font-display font-semibold text-2xl text-brand-navy">{s.value}</div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mt-1 font-medium">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-brand-line bg-white overflow-hidden shadow-sm">
            {rows.length === 0 ? (
              <div className="py-16 text-center text-slate-400" data-testid="gsc-empty">
                Aucune donnée sur cette période. Google met parfois quelques jours à remonter les statistiques d'un nouveau site.
              </div>
            ) : (
              <Table data-testid="gsc-table">
                <TableHeader>
                  <TableRow className="border-brand-line hover:bg-transparent">
                    <TableHead className="text-slate-500">Page</TableHead>
                    <TableHead className="text-slate-500 text-right">Clics</TableHead>
                    <TableHead className="text-slate-500 text-right">Impressions</TableHead>
                    <TableHead className="text-slate-500 text-right">CTR</TableHead>
                    <TableHead className="text-slate-500 text-right">Position</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.page}
                      data-testid={`gsc-row-${pageLabel(r.page)}`}
                      className="border-brand-line hover:bg-brand-mist/60"
                    >
                      <TableCell className="font-medium text-brand-navy max-w-[340px] truncate">
                        {pageLabel(r.page)}
                      </TableCell>
                      <TableCell className="text-right text-slate-700">{r.clicks}</TableCell>
                      <TableCell className="text-right text-slate-700">
                        {r.impressions.toLocaleString("fr-FR")}
                      </TableCell>
                      <TableCell className="text-right text-slate-700">
                        {(r.ctr * 100).toFixed(1)} %
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            "font-semibold " +
                            (r.position <= 10
                              ? "text-emerald-600"
                              : r.position <= 30
                              ? "text-brand-golddark"
                              : "text-slate-500")
                          }
                        >
                          {r.position.toFixed(1)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {queries && (
            <div className="mt-10">
              <h3 className="font-display font-semibold text-xl text-brand-navy mb-1">
                Requêtes de recherche
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Les mots-clés qui affichent votre site dans Google, avec leur position moyenne.
              </p>
              <div className="rounded-2xl border border-brand-line bg-white overflow-hidden shadow-sm">
                {queries.rows.length === 0 ? (
                  <div className="py-16 text-center text-slate-400" data-testid="gsc-queries-empty">
                    Aucune requête sur cette période.
                  </div>
                ) : (
                  <Table data-testid="gsc-queries-table">
                    <TableHeader>
                      <TableRow className="border-brand-line hover:bg-transparent">
                        <TableHead className="text-slate-500">Requête</TableHead>
                        <TableHead className="text-slate-500 text-right">Clics</TableHead>
                        <TableHead className="text-slate-500 text-right">Impressions</TableHead>
                        <TableHead className="text-slate-500 text-right">Position</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queries.rows
                        .slice()
                        .sort((a, b) => b.clicks - a.clicks || b.impressions - a.impressions)
                        .slice(0, 50)
                        .map((r) => (
                          <TableRow
                            key={r.query}
                            data-testid={`gsc-query-${r.query}`}
                            className="border-brand-line hover:bg-brand-mist/60"
                          >
                            <TableCell className="font-medium text-brand-navy max-w-[340px] truncate">
                              {r.query}
                            </TableCell>
                            <TableCell className="text-right text-slate-700">{r.clicks}</TableCell>
                            <TableCell className="text-right text-slate-700">
                              {r.impressions.toLocaleString("fr-FR")}
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={
                                  "font-semibold " +
                                  (r.position <= 10
                                    ? "text-emerald-600"
                                    : r.position <= 30
                                    ? "text-brand-golddark"
                                    : "text-slate-500")
                                }
                              >
                                {r.position.toFixed(1)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}

          {data.fetched_at && (
            <p className="text-xs text-slate-400 mt-3">
              Données {data.cached ? "en cache" : "fraîches"} — relevé du{" "}
              {new Date(data.fetched_at).toLocaleString("fr-FR")}. Google finalise ses données
              avec environ 2 jours de retard.
            </p>
          )}
        </>
      )}
    </div>
  );
}
